import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MagnifyingGlass, ChatsCircle, FileText, Buildings,
  TrendUp, Handshake, MapPin, CheckCircle,
} from "@phosphor-icons/react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1770017408222-dc83f61d9725?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxob3RlbCUyMGV4dGVyaW9yJTIwbW9kZXJufGVufDB8fHx8MTc4NDQ3NDc3MHww&ixlib=rb-4.1.0&q=85";
const TEAM_IMG =
  "https://images.pexels.com/photos/36733421/pexels-photo-36733421.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CREW_IMG =
  "https://images.pexels.com/photos/1078879/pexels-photo-1078879.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

export default function Landing() {
  const steps = [
    { icon: MagnifyingGlass, t: "Search direct", d: "Find the right hotels by city, rate, brand and amenities — and connect straight with the GM." },
    { icon: FileText, t: "Submit an RFP", d: "Send room-block requests with dates, budget and terms in one click." },
    { icon: ChatsCircle, t: "Negotiate GM-to-team", d: "Counter offers in a live thread. Talk directly with the general manager." },
    { icon: Handshake, t: "Sign & book", d: "Lock the negotiated rate in a digital contract, then book against it." },
  ];

  const metrics = [
    { k: "1", v: "Platform for RFPs to bookings" },
    { k: "3", v: "Pilot states: OK · KS · TX" },
    { k: "GM", v: "Direct GM-to-company deals" },
    { k: "100%", v: "Yours: rate & guest relationship" },
  ];

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#E5E7EB]">
        <div className="grain" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div initial="hidden" animate="show" variants={fade} custom={0}>
              <span className="overline text-[#002FA7]">B2B Negotiated-Rate Marketplace</span>
            </motion.div>
            <motion.h1
              initial="hidden" animate="show" variants={fade} custom={1}
              className="font-display font-black text-5xl md:text-6xl text-[#111827] mt-5"
            >
              Connect directly.<br />
              Negotiate <span className="text-[#002FA7]">confidently.</span> Book smarter.
            </motion.h1>
            <motion.p
              initial="hidden" animate="show" variants={fade} custom={2}
              className="text-lg text-[#4B5563] mt-6 max-w-xl leading-relaxed"
            >
              RateBridge brings companies and hotel general managers together to simplify
              corporate RFPs, live negotiations, digital contracting, and direct hotel
              bookings — all in one seamless platform.
            </motion.p>
            <motion.div
              initial="hidden" animate="show" variants={fade} custom={3}
              className="flex flex-wrap items-center gap-4 mt-9"
            >
              <Link
                to="/register"
                data-testid="hero-company-cta"
                className="group inline-flex items-center gap-2 bg-[#002FA7] text-white px-6 py-3.5 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98]"
              >
                <Buildings size={18} weight="bold" /> I'm a Company
                <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/register"
                data-testid="hero-hotel-cta"
                className="inline-flex items-center gap-2 border border-[#111827] text-[#111827] px-6 py-3.5 font-semibold hover:bg-[#111827] hover:text-white transition-colors active:scale-[0.98]"
              >
                <Handshake size={18} weight="bold" /> I'm a Management Company
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative border border-[#E5E7EB]">
              <img src={HERO_IMG} alt="Modern hotel" className="w-full h-[420px] object-cover" />
              <div className="absolute -bottom-6 -left-6 bg-white border border-[#E5E7EB] p-5 shadow-xl w-56 hidden md:block">
                <span className="overline text-[#4B5563]">Negotiated rate</span>
                <div className="font-display font-black text-4xl text-[#111827] mt-1">$88<span className="text-base font-mono text-[#4B5563]">/night</span></div>
                <div className="flex items-center gap-1.5 text-[#047857] text-xs font-semibold mt-2">
                  <CheckCircle size={14} weight="fill" /> 12-room block · 30 nights
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* metrics strip */}
        <div className="border-t border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 grid-skeleton border-l border-[#E5E7EB]">
            {metrics.map((m) => (
              <div key={m.v} className="p-6 md:p-8">
                <div className="font-display font-black text-3xl md:text-4xl text-[#002FA7]">{m.k}</div>
                <div className="text-sm text-[#4B5563] mt-1">{m.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="overline text-[#002FA7]">How it works</span>
            <h2 className="font-display font-bold text-4xl text-[#111827] mt-3 max-w-lg">
              The whole hotel program, one bridge.
            </h2>
          </div>
          <p className="text-[#4B5563] max-w-sm">
            From discovery to signed contract to booked room nights — every step lives
            in a single workflow.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 grid-skeleton border-l border-t border-[#E5E7EB] mt-10">
          {steps.map((s, i) => (
            <motion.div
              key={s.t}
              initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
              className="p-8 bg-white"
            >
              <div className="flex items-center justify-between">
                <s.icon size={34} weight="duotone" color="#002FA7" />
                <span className="font-mono text-sm text-[#9CA3AF]">0{i + 1}</span>
              </div>
              <h3 className="font-display font-bold text-xl text-[#111827] mt-5">{s.t}</h3>
              <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* AUDIENCE BENTO */}
      <section className="bg-[#F3F4F6] border-y border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20">
          <span className="overline text-[#002FA7]">Built for both sides</span>
          <div className="grid lg:grid-cols-12 gap-6 mt-8">
            <div className="lg:col-span-7 bg-[#111827] text-white p-10 relative overflow-hidden">
              <div className="grain" />
              <Buildings size={40} weight="duotone" color="#7C93E6" />
              <h3 className="font-display font-bold text-3xl mt-5">For Companies</h3>
              <p className="text-white/70 mt-3 max-w-md">
                For construction crews, healthcare systems, energy and manufacturing teams
                on the move — build lasting hotel partnerships and manage every corporate
                program in one confident, organized place.
              </p>
              <ul className="grid sm:grid-cols-2 gap-3 mt-7">
                {["Multi-city hotel search", "One-click RFPs", "Direct rate negotiation", "Digital contracts & booking"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-white/90">
                    <CheckCircle size={18} weight="fill" color="#34D399" /> {f}
                  </li>
                ))}
              </ul>
              <img src={CREW_IMG} alt="Crew" className="absolute right-0 bottom-0 w-40 h-40 object-cover opacity-20 hidden md:block" />
            </div>

            <div className="lg:col-span-5 bg-white border border-[#E5E7EB] p-10">
              <Handshake size={40} weight="duotone" color="#002FA7" />
              <h3 className="font-display font-bold text-3xl mt-5 text-[#111827]">For Hotel Owners/GMs</h3>
              <p className="text-[#4B5563] mt-3">
                Independent and branded hotels build direct relationships with corporate
                buyers — you set the rate, own the guest relationship, and grow repeat business.
              </p>
              <ul className="space-y-3 mt-7">
                {["Receive qualified RFPs", "Counter-offer in real time", "Send digital contracts", "Track revenue & analytics"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-[#111827]">
                    <CheckCircle size={18} weight="fill" color="#002FA7" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PILOT */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative border border-[#E5E7EB]">
          <img src={TEAM_IMG} alt="Team negotiating" className="w-full h-[360px] object-cover" />
        </div>
        <div>
          <span className="overline text-[#002FA7]">Go-to-market</span>
          <h2 className="font-display font-bold text-4xl text-[#111827] mt-3">
            Launching across the heartland.
          </h2>
          <p className="text-[#4B5563] mt-4 leading-relaxed">
            RateBridge is piloting with independent hotel owners across Oklahoma,
            Kansas and Texas — then expanding into a national network of direct
            negotiated-rate programs.
          </p>
          <div className="flex items-center gap-6 mt-8">
            {["Oklahoma City", "Wichita", "Dallas"].map((c) => (
              <div key={c} className="flex items-center gap-2 text-[#111827] font-semibold">
                <MapPin size={18} weight="fill" color="#002FA7" /> {c}
              </div>
            ))}
          </div>
          <Link
            to="/register"
            data-testid="pilot-cta"
            className="inline-flex items-center gap-2 mt-9 bg-[#002FA7] text-white px-6 py-3.5 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98]"
          >
            Get started <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#111827] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 flex flex-col md:flex-row justify-between gap-8">
          <div>
            <div className="font-display font-bold text-2xl">Rate<span className="text-[#7C93E6]">Bridge</span></div>
            <p className="text-white/60 mt-2 max-w-xs text-sm">
              Direct hotel sales, corporate contracting and negotiated-rate marketplace.
            </p>
          </div>
          <div className="flex items-center gap-2 text-white/50 text-sm self-end">
            <TrendUp size={16} weight="bold" /> © 2026 RateBridge. B2B lodging, reimagined.
          </div>
        </div>
      </footer>
    </div>
  );
}
